import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import '../styles/SuperAdminSettingsPage.css';
import { API_BASE_URL, getAuthHeaders, fetchDbMigrationConnection, saveDbMigrationConnection, fetchDbMigrationStatus, runDbMigrations } from '../utils/api';

function SuperAdminSettingsPage() {
  const { user } = useAuth();
  const [message, setMessage] = useState({ type: '', text: '' });
  const [newSuffix, setNewSuffix] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [newIsPublic, setNewIsPublic] = useState(true);
  const [adding, setAdding] = useState(false);
  const [horizons, setHorizons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deletingSuffix, setDeletingSuffix] = useState(null);
  const [deleteError, setDeleteError] = useState(null);
  const [togglingVisibility, setTogglingVisibility] = useState(null);
  const [transferModal, setTransferModal] = useState(null); // { suffix, currentOwnerId }
  const [deleteConfirmModal, setDeleteConfirmModal] = useState(null); // { suffix } when open
  const [deleteConfirmTyped, setDeleteConfirmTyped] = useState('');
  const [removePeopleModal, setRemovePeopleModal] = useState(null); // { suffix, users } when open
  const [removingPeopleSuffix, setRemovingPeopleSuffix] = useState(null);
  const [loadingPeopleSuffix, setLoadingPeopleSuffix] = useState(null);
  const [allUsers, setAllUsers] = useState([]);
  const [transferring, setTransferring] = useState(false);
  const [ownerMap, setOwnerMap] = useState({}); // { owner_id: { id, email, user_type } }
  const [migrationStatus, setMigrationStatus] = useState(null);
  const [migrationLoading, setMigrationLoading] = useState(false);
  const [migrationRunning, setMigrationRunning] = useState(false);
  const [migrationError, setMigrationError] = useState('');
  const [postgresConnection, setPostgresConnection] = useState({ configured: false, hostHint: null });
  const [postgresUriInput, setPostgresUriInput] = useState('');
  const [savingPostgresConnection, setSavingPostgresConnection] = useState(false);
  const [connectionError, setConnectionError] = useState('');

  const loadMigrationStatus = useCallback(async () => {
    try {
      setMigrationLoading(true);
      setMigrationError('');
      const json = await fetchDbMigrationStatus();
      setMigrationStatus(json);
    } catch (err) {
      setMigrationError(err.message || 'Failed to load migration status');
      setMigrationStatus(null);
    } finally {
      setMigrationLoading(false);
    }
  }, []);

  const refreshDbMigrations = useCallback(async () => {
    try {
      setConnectionError('');
      const json = await fetchDbMigrationConnection();
      setPostgresConnection({
        configured: Boolean(json?.configured),
        hostHint: json?.hostHint || null,
      });
      if (json?.configured) {
        await loadMigrationStatus();
      } else {
        setMigrationStatus({ configured: false });
        setMigrationError('');
      }
    } catch (err) {
      setConnectionError(err.message || 'Failed to load Postgres connection setting');
      setPostgresConnection({ configured: false, hostHint: null });
      setMigrationStatus({ configured: false });
    }
  }, [loadMigrationStatus]);

  useEffect(() => {
    if (user?.accountType === 'superadmin') {
      loadHorizons();
      refreshDbMigrations();
    }
  }, [user, refreshDbMigrations]);

  const loadHorizons = async () => {
    try {
      setLoading(true);
      const headers = await getAuthHeaders();
      // Load horizons and enriched instances in parallel
      const [horizonsResp, instancesResp] = await Promise.all([
        fetch(`${API_BASE_URL}/casc/v1/api/horizon-management/horizons`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...headers },
          body: JSON.stringify({}),
        }),
        fetch(`${API_BASE_URL}/casc/v1/api/instances`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...headers },
          body: JSON.stringify({}),
        })
      ]);

      if (horizonsResp.ok) {
        const data = await horizonsResp.json();
        setHorizons(data.horizons || []);
      } else {
        setMessage({ type: 'error', text: 'Failed to load horizons' });
      }

      // Build owner map from enriched instances data
      if (instancesResp.ok) {
        const instData = await instancesResp.json();
        const map = {};
        for (const inst of (instData.instances || [])) {
          if (inst.owner) {
            map[inst.owner_id] = inst.owner;
          }
        }
        setOwnerMap(map);
      }
    } catch (error) {
      console.error('Error loading horizons:', error);
      setMessage({ type: 'error', text: 'Error loading horizons' });
    } finally {
      setLoading(false);
    }
  };

  const handleAddInstance = async (e) => {
    e.preventDefault();

    if (!newSuffix.trim()) {
      setMessage({ type: 'error', text: 'Suffix is required' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
      return;
    }

    try {
      setAdding(true);
      setMessage({ type: '', text: '' });
      
      const { getAuthHeaders } = await import('../utils/api');
      const headers = await getAuthHeaders();
      
      const response = await fetch(`${API_BASE_URL}/casc/v1/api/instances/add`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...headers,
        },
        body: JSON.stringify({
          suffix: newSuffix.trim(),
          description: newDescription.trim() || '',
          is_public: newIsPublic,
        }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setMessage({ type: 'success', text: data.message || 'Horizon added successfully' });
        setTimeout(() => setMessage({ type: '', text: '' }), 3000);
        setNewSuffix('');
        setNewDescription('');
        await loadHorizons();
      } else {
        setMessage({ type: 'error', text: data.message || data.error || 'Failed to add horizon' });
        setTimeout(() => setMessage({ type: '', text: '' }), 5000);
      }
    } catch (error) {
      console.error('Error adding horizon:', error);
      setMessage({ type: 'error', text: 'Error adding horizon' });
      setTimeout(() => setMessage({ type: '', text: '' }), 5000);
    } finally {
      setAdding(false);
    }
  };

  const openDeleteConfirmModal = (suffix) => {
    setDeleteConfirmModal({ suffix });
    setDeleteConfirmTyped('');
  };

  const closeDeleteConfirmModal = () => {
    setDeleteConfirmModal(null);
    setDeleteConfirmTyped('');
  };

  const openRemovePeopleModal = async (suffix) => {
    try {
      setLoadingPeopleSuffix(suffix);
      setMessage({ type: '', text: '' });
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}/casc/v1/api/horizon-management/horizon-users`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...headers },
        body: JSON.stringify({ suffix }),
      });
      const data = await response.json();
      if (!response.ok || !data.success) {
        setMessage({ type: 'error', text: data.message || data.error || 'Failed to load assigned users' });
        setTimeout(() => setMessage({ type: '', text: '' }), 5000);
        return;
      }
      const users = data.users || [];
      if (users.length === 0) {
        setMessage({ type: 'success', text: `No users assigned to ${suffix}` });
        setTimeout(() => setMessage({ type: '', text: '' }), 3000);
        setDeleteError(null);
        return;
      }
      setRemovePeopleModal({ suffix, users });
    } catch (error) {
      console.error('Error loading horizon users:', error);
      setMessage({ type: 'error', text: 'Error loading assigned users' });
      setTimeout(() => setMessage({ type: '', text: '' }), 5000);
    } finally {
      setLoadingPeopleSuffix(null);
    }
  };

  const closeRemovePeopleModal = () => {
    setRemovePeopleModal(null);
  };

  const handleRemovePeople = async (suffix) => {
    try {
      setRemovingPeopleSuffix(suffix);
      setMessage({ type: '', text: '' });
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}/casc/v1/api/horizon-management/remove-all-users`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...headers },
        body: JSON.stringify({ suffix }),
      });
      const data = await response.json();
      if (response.ok && data.success) {
        setMessage({
          type: 'success',
          text: data.message || `Removed users from ${suffix}`
        });
        setTimeout(() => setMessage({ type: '', text: '' }), 4000);
        setDeleteError(null);
        closeRemovePeopleModal();
        await loadHorizons();
      } else {
        setMessage({ type: 'error', text: data.message || data.error || 'Failed to remove people' });
        setTimeout(() => setMessage({ type: '', text: '' }), 5000);
      }
    } catch (error) {
      console.error('Error removing people from horizon:', error);
      setMessage({ type: 'error', text: 'Error removing people' });
      setTimeout(() => setMessage({ type: '', text: '' }), 5000);
    } finally {
      setRemovingPeopleSuffix(null);
    }
  };

  const handleDeleteHorizon = async (suffix) => {
    closeDeleteConfirmModal();
    try {
      setDeletingSuffix(suffix);
      setDeleteError(null);
      setMessage({ type: '', text: '' });
      
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}/casc/v1/api/horizon-management/delete-horizon`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...headers,
        },
        body: JSON.stringify({ suffix }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setMessage({ type: 'success', text: data.message || `Horizon ${suffix} deleted successfully` });
        setTimeout(() => setMessage({ type: '', text: '' }), 3000);
        await loadHorizons();
      } else {
        if (data.users && data.users.length > 0) {
          setDeleteError({
            suffix: suffix,
            users: data.users,
            message: data.message || 'Cannot delete horizon with assigned users'
          });
        } else {
          setMessage({ type: 'error', text: data.message || data.error || 'Failed to delete horizon' });
          setTimeout(() => setMessage({ type: '', text: '' }), 5000);
        }
      }
    } catch (error) {
      console.error('Error deleting horizon:', error);
      setMessage({ type: 'error', text: 'Error deleting horizon' });
      setTimeout(() => setMessage({ type: '', text: '' }), 5000);
    } finally {
      setDeletingSuffix(null);
    }
  };

  const handleToggleVisibility = async (suffix, currentIsPublic) => {
    try {
      setTogglingVisibility(suffix);
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}/casc/v1/api/instances/set-visibility`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...headers },
        body: JSON.stringify({ suffix, is_public: !currentIsPublic }),
      });
      const data = await response.json();
      if (response.ok && data.success) {
        setMessage({ type: 'success', text: data.message || `Visibility updated` });
        setTimeout(() => setMessage({ type: '', text: '' }), 3000);
        await loadHorizons();
      } else {
        setMessage({ type: 'error', text: data.message || data.error || 'Failed to update visibility' });
        setTimeout(() => setMessage({ type: '', text: '' }), 5000);
      }
    } catch (error) {
      console.error('Error toggling visibility:', error);
      setMessage({ type: 'error', text: 'Error updating visibility' });
      setTimeout(() => setMessage({ type: '', text: '' }), 5000);
    } finally {
      setTogglingVisibility(null);
    }
  };

  const openTransferModal = async (suffix, currentOwnerId) => {
    setTransferModal({ suffix, currentOwnerId });
    // Load all users for the selector
    try {
      const headers = await getAuthHeaders();
      const resp = await fetch(`${API_BASE_URL}/casc/v1/api/user-invites/list`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...headers },
        body: JSON.stringify({}),
      });
      const data = await resp.json();
      if (resp.ok) {
        setAllUsers(data.users || data.data || []);
      }
    } catch (_) {
      setAllUsers([]);
    }
  };

  const handleTransferOwnership = async (newOwnerId) => {
    if (!transferModal) return;
    try {
      setTransferring(true);
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}/casc/v1/api/instances/transfer-ownership`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...headers },
        body: JSON.stringify({ suffix: transferModal.suffix, new_owner_id: newOwnerId }),
      });
      const data = await response.json();
      if (response.ok && data.success) {
        setMessage({ type: 'success', text: data.message || 'Ownership transferred' });
        setTimeout(() => setMessage({ type: '', text: '' }), 3000);
        setTransferModal(null);
        await loadHorizons();
      } else {
        setMessage({ type: 'error', text: data.message || data.error || 'Failed to transfer ownership' });
        setTimeout(() => setMessage({ type: '', text: '' }), 5000);
      }
    } catch (error) {
      console.error('Error transferring ownership:', error);
      setMessage({ type: 'error', text: 'Error transferring ownership' });
      setTimeout(() => setMessage({ type: '', text: '' }), 5000);
    } finally {
      setTransferring(false);
    }
  };

  const handleSavePostgresConnection = async (e) => {
    e.preventDefault();
    const uri = postgresUriInput.trim();
    if (!uri) {
      setConnectionError('Enter the Postgres connection URI from Supabase (Database → Connection string → URI).');
      return;
    }
    try {
      setSavingPostgresConnection(true);
      setConnectionError('');
      const json = await saveDbMigrationConnection(uri);
      setPostgresConnection({
        configured: true,
        hostHint: json?.hostHint || null,
      });
      setPostgresUriInput('');
      setMessage({
        type: 'success',
        text: json?.message || 'Postgres connection saved.',
      });
      setTimeout(() => setMessage({ type: '', text: '' }), 6000);
      await loadMigrationStatus();
    } catch (err) {
      setConnectionError(err.message || 'Failed to save Postgres connection');
    } finally {
      setSavingPostgresConnection(false);
    }
  };

  const handleRunMigrations = async () => {
    const pending = migrationStatus?.status?.pending || [];
    if (!pending.length) return;
    if (!window.confirm(`Run ${pending.length} pending migration(s)? The server may briefly lock schema during apply.`)) {
      return;
    }
    try {
      setMigrationRunning(true);
      setMigrationError('');
      const json = await runDbMigrations();
      const applied = json?.result?.applied || [];
      setMessage({
        type: 'success',
        text: applied.length
          ? `Applied ${applied.length} migration(s): ${applied.join(', ')}`
          : (json?.result?.message || 'Migrations complete'),
      });
      setTimeout(() => setMessage({ type: '', text: '' }), 8000);
      await loadMigrationStatus();
    } catch (err) {
      setMigrationError(err.message || 'Migration run failed');
      setMessage({ type: 'error', text: err.message || 'Migration run failed' });
      setTimeout(() => setMessage({ type: '', text: '' }), 8000);
      await loadMigrationStatus();
    } finally {
      setMigrationRunning(false);
    }
  };

  // Hide this page from non-superadmin users
  if (user?.accountType !== 'superadmin') {
    return null;
  }

  return (
    <div className="superadmin-settings-page">
      <div className="card full-width">
        <h2><i className="fas fa-shield-alt"></i> Superadmin Settings</h2>
        <p className="section-description">
          Create new horizons. Users can select their active horizon in User Settings.
        </p>

        {message.text && (
          <div className={`alert alert-${message.type}`}>
            <i className={`fas fa-${message.type === 'success' ? 'check-circle' : 'exclamation-circle'}`}></i>
            {message.text}
          </div>
        )}

        {/* Database migrations */}
        <div className="settings-section db-migrations-section">
          <h3><i className="fas fa-database"></i> Database migrations</h3>
          <p className="section-description">
            SQL schema migrations ship with each server deploy (<code>db/migrations/</code>).
            Configure the direct Postgres connection for this instance, then apply pending migrations from here or on server startup.
          </p>

          <form className="db-migration-connection-form" onSubmit={handleSavePostgresConnection}>
            <label htmlFor="postgres-direct-uri">
              Postgres connection URI
              <span className="field-hint">Supabase Dashboard → Database → Connection string (URI). Not the HTTP project URL.</span>
            </label>
            {postgresConnection.configured && postgresConnection.hostHint && (
              <p className="db-migration-connection-saved">
                <i className="fas fa-check-circle" /> Configured for <code>{postgresConnection.hostHint}</code>.
                Enter a new URI below to replace it.
              </p>
            )}
            <input
              id="postgres-direct-uri"
              type="password"
              className="form-control"
              autoComplete="off"
              placeholder="postgresql://postgres:…@db.….supabase.co:5432/postgres"
              value={postgresUriInput}
              onChange={(e) => setPostgresUriInput(e.target.value)}
              disabled={savingPostgresConnection}
            />
            {connectionError && (
              <div className="alert alert-error">{connectionError}</div>
            )}
            <button
              type="submit"
              className="btn btn-secondary"
              disabled={savingPostgresConnection || !postgresUriInput.trim()}
            >
              {savingPostgresConnection ? (
                <><i className="fas fa-spinner fa-spin" /> Testing &amp; saving…</>
              ) : (
                <><i className="fas fa-save" /> Save Postgres connection</>
              )}
            </button>
          </form>

          {!postgresConnection.configured ? (
            <div className="alert alert-error db-migration-config-required">
              <i className="fas fa-exclamation-circle" /> Save the Postgres connection URI above before viewing migration status or running migrations.
            </div>
          ) : migrationLoading ? (
            <div className="loading"><i className="fas fa-spinner fa-spin" /> Loading migration status…</div>
          ) : (
            <div className="db-migration-panel">
              <div className="db-migration-stats">
                <div className="db-migration-stat">
                  <span className="label">Applied (DB)</span>
                  <span className="value">
                    {migrationStatus?.status?.stateReadable
                      ? String(migrationStatus.status.recorded).padStart(3, '0')
                      : '—'}
                  </span>
                </div>
                <div className="db-migration-stat">
                  <span className="label">Shipped (server)</span>
                  <span className="value">
                    {migrationStatus?.status?.maxOnDisk
                      ? String(migrationStatus.status.maxOnDisk).padStart(3, '0')
                      : '—'}
                  </span>
                </div>
                <div className="db-migration-stat">
                  <span className="label">Pending</span>
                  <span className={`value ${(migrationStatus?.status?.pending?.length || 0) > 0 ? 'pending' : 'ok'}`}>
                    {migrationStatus?.status?.pending?.length ?? 0}
                  </span>
                </div>
              </div>
              {migrationStatus?.status?.repoBehind && (
                <div className="alert alert-error">
                  DB level is ahead of migrations in this deploy — the server package may be older than the database.
                </div>
              )}
              {migrationStatus?.status?.upToDate && (
                <p className="db-migration-ok"><i className="fas fa-check-circle" /> Schema is up to date.</p>
              )}
              {(migrationStatus?.status?.pending || []).length > 0 && (
                <ul className="db-migration-pending-list">
                  {migrationStatus.status.pending.map((p) => (
                    <li key={p.file}><code>{p.file}</code></li>
                  ))}
                </ul>
              )}
              {migrationError && (
                <div className="alert alert-error">{migrationError}</div>
              )}
              <div className="db-migration-actions">
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={
                    migrationRunning
                    || !(migrationStatus?.status?.pending?.length)
                    || migrationStatus?.running
                  }
                  onClick={handleRunMigrations}
                >
                  {migrationRunning ? (
                    <><i className="fas fa-spinner fa-spin" /> Running migrations…</>
                  ) : (
                    <><i className="fas fa-play" /> Run pending migrations</>
                  )}
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  disabled={migrationLoading || migrationRunning}
                  onClick={refreshDbMigrations}
                >
                  <i className="fas fa-sync" /> Refresh
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Horizons List */}
        <div className="settings-section">
          <h3><i className="fas fa-list"></i> All Horizons</h3>
          {loading ? (
            <div className="loading">
              <i className="fas fa-spinner fa-spin"></i> Loading horizons...
            </div>
          ) : horizons.length === 0 ? (
            <div className="empty-state">
              <p>No horizons found. Create one below.</p>
            </div>
          ) : (
            <div className="horizons-list">
              {horizons.map((horizon) => {
                const isPublic = horizon.is_public !== false;
                const ownerInfo = horizon.owner_id ? ownerMap[horizon.owner_id] : null;
                const ownerEmail = ownerInfo?.email || null;
                const assignedCount = Number(horizon.assigned_user_count) || 0;
                const hasAssignedPeople = assignedCount > 0;
                const isOwner = Boolean(user?.id && horizon.owner_id === user.id);
                return (
                <div key={horizon.id} className={`horizon-item ${horizon.is_deleted ? 'deleted' : ''}`}>
                  <div className="horizon-item-info">
                    <div className="horizon-header">
                      <span className="horizon-name">{horizon.suffix}</span>
                      {horizon.is_deleted && (
                        <span className="deleted-badge">
                          <i className="fas fa-trash"></i> Deleted
                        </span>
                      )}
                      <span className={`visibility-badge ${isPublic ? 'public' : 'private'}`}>
                        <i className={`fas fa-${isPublic ? 'globe' : 'lock'}`}></i>
                        {isPublic ? 'Public' : 'Private'}
                      </span>
                    </div>
                    {horizon.description && (
                      <p className="horizon-description-text">{horizon.description}</p>
                    )}
                    <div className="horizon-meta">
                      <span>Created: {new Date(horizon.created_at).toLocaleDateString()}</span>
                      {ownerEmail && (
                        <span className="horizon-owner-info">
                          <i className="fas fa-user"></i> Owner: {ownerEmail}
                        </span>
                      )}
                      {hasAssignedPeople && (
                        <span className="horizon-people-count">
                          <i className="fas fa-users"></i> {assignedCount} assigned
                        </span>
                      )}
                    </div>
                    {deleteError && deleteError.suffix === horizon.suffix && (
                      <div className="delete-error-message">
                        <i className="fas fa-exclamation-triangle"></i>
                        <strong>{deleteError.message}</strong>
                        <div className="assigned-users-list">
                          <p>Assigned users:</p>
                          <ul>
                            {deleteError.users.map((u) => (
                              <li key={u.id}>{u.email} ({u.user_type})</li>
                            ))}
                          </ul>
                          <p className="hint">Use Remove People on this row, then Delete.</p>
                        </div>
                        <button
                          className="btn btn-sm btn-secondary"
                          onClick={() => setDeleteError(null)}
                        >
                          Dismiss
                        </button>
                        {hasAssignedPeople && (
                          <button
                            className="btn btn-sm btn-warning"
                            onClick={() => openRemovePeopleModal(horizon.suffix)}
                            disabled={loadingPeopleSuffix === horizon.suffix || removingPeopleSuffix === horizon.suffix}
                          >
                            {loadingPeopleSuffix === horizon.suffix ? (
                              <i className="fas fa-spinner fa-spin"></i>
                            ) : (
                              <>
                                <i className="fas fa-user-minus"></i> Remove People
                              </>
                            )}
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="horizon-item-actions">
                    {horizon.is_deleted ? (
                      <button
                        className="btn btn-sm btn-secondary"
                        onClick={() => openTransferModal(horizon.suffix, horizon.owner_id)}
                        title="Reassign ownership of this deleted horizon (for stuck account deletion)"
                      >
                        <i className="fas fa-exchange-alt"></i> Transfer
                      </button>
                    ) : (
                      <>
                        <button
                          className={`btn btn-sm ${isPublic ? 'btn-secondary' : 'btn-primary'}`}
                          onClick={() => handleToggleVisibility(horizon.suffix, isPublic)}
                          disabled={togglingVisibility === horizon.suffix}
                          title={isPublic ? 'Make private' : 'Make public'}
                        >
                          {togglingVisibility === horizon.suffix ? (
                            <i className="fas fa-spinner fa-spin"></i>
                          ) : (
                            <>
                              <i className={`fas fa-${isPublic ? 'lock' : 'globe'}`}></i>
                              {isPublic ? 'Make Private' : 'Make Public'}
                            </>
                          )}
                        </button>
                        <button
                          className="btn btn-sm btn-secondary"
                          onClick={() => openTransferModal(horizon.suffix, horizon.owner_id)}
                          title="Transfer ownership"
                        >
                          <i className="fas fa-exchange-alt"></i> Transfer
                        </button>
                        {hasAssignedPeople && (
                          <button
                            className="btn btn-sm btn-warning"
                            onClick={() => openRemovePeopleModal(horizon.suffix)}
                            disabled={loadingPeopleSuffix === horizon.suffix || removingPeopleSuffix === horizon.suffix}
                            title="Remove all assigned users so this horizon can be deleted"
                          >
                            {loadingPeopleSuffix === horizon.suffix || removingPeopleSuffix === horizon.suffix ? (
                              <i className="fas fa-spinner fa-spin"></i>
                            ) : (
                              <>
                                <i className="fas fa-user-minus"></i> Remove People
                              </>
                            )}
                          </button>
                        )}
                        {isOwner && (
                          <button
                            className="btn btn-sm btn-danger"
                            onClick={() => openDeleteConfirmModal(horizon.suffix)}
                            disabled={deletingSuffix === horizon.suffix}
                            title="Delete this horizon (you own it)"
                          >
                            {deletingSuffix === horizon.suffix ? (
                              <i className="fas fa-spinner fa-spin"></i>
                            ) : (
                              <>
                                <i className="fas fa-trash"></i> Delete
                              </>
                            )}
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Add New Horizon */}
        <div className="settings-section">
          <h3><i className="fas fa-plus-circle"></i> Add New Horizon</h3>
          <form onSubmit={handleAddInstance} className="add-instance-form">
            <div className="form-group">
              <label htmlFor="newSuffix">Horizon *</label>
              <input
                type="text"
                id="newSuffix"
                value={newSuffix}
                onChange={(e) => setNewSuffix(e.target.value)}
                placeholder="e.g., new_instance"
                required
                pattern="[a-z0-9_]+"
                title="Lowercase letters, numbers, and underscores only"
              />
              <small>Lowercase letters, numbers, and underscores only (e.g., rsmg_sales)</small>
            </div>
            <div className="form-group">
              <label htmlFor="newDescription">Description</label>
              <input
                type="text"
                id="newDescription"
                value={newDescription}
                onChange={(e) => setNewDescription(e.target.value)}
                placeholder="Optional description"
              />
            </div>
            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={newIsPublic}
                  onChange={(e) => setNewIsPublic(e.target.checked)}
                />
                <span>Public</span>
                <small className="checkbox-hint">Public horizons are visible to all users. Private horizons are only visible to the owner.</small>
              </label>
            </div>
            <button type="submit" className="btn btn-primary" disabled={adding}>
              {adding ? (
                <>
                  <i className="fas fa-spinner fa-spin"></i> Adding...
                </>
              ) : (
                <>
                  <i className="fas fa-plus"></i> Add Horizon
                </>
              )}
            </button>
          </form>
        </div>
      </div>

      {/* Delete Horizon confirmation modal: must type DELETE to enable Delete */}
      {deleteConfirmModal && (
        <div className="modal-backdrop">
          <div className="delete-horizon-modal transfer-modal">
            <div className="modal-header">
              <h3>Delete horizon — {deleteConfirmModal.suffix}</h3>
              <button type="button" className="btn ghost" onClick={closeDeleteConfirmModal}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to delete this horizon? This action cannot be undone.</p>
              <p className="delete-confirm-hint">Type <strong>DELETE</strong> below to enable the Delete button.</p>
              <input
                type="text"
                className="delete-confirm-input"
                placeholder="Type DELETE"
                value={deleteConfirmTyped}
                onChange={(e) => setDeleteConfirmTyped(e.target.value)}
                autoComplete="off"
                autoFocus
              />
              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={closeDeleteConfirmModal}>
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  disabled={deleteConfirmTyped !== 'DELETE'}
                  onClick={() => handleDeleteHorizon(deleteConfirmModal.suffix)}
                >
                  <i className="fas fa-trash"></i> Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Transfer Ownership Modal */}
      {transferModal && (
        <div className="modal-backdrop">
          <div className="transfer-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Transfer Ownership — {transferModal.suffix}</h3>
              <button className="btn ghost" onClick={() => setTransferModal(null)}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="modal-body">
              <p>Select a user to transfer ownership of this horizon to:</p>
              <div className="transfer-user-list">
                {allUsers.filter(u => u.id !== transferModal.currentOwnerId).map(u => (
                  <button
                    key={u.id}
                    className="transfer-user-option"
                    onClick={() => handleTransferOwnership(u.id)}
                    disabled={transferring}
                  >
                    <i className="fas fa-user"></i>
                    <span>{u.email}</span>
                    <span className="user-type-badge">{u.user_type || u.accountType}</span>
                  </button>
                ))}
                {allUsers.filter(u => u.id !== transferModal.currentOwnerId).length === 0 && (
                  <div className="empty-state"><p>No other users available.</p></div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Remove People confirmation modal */}
      {removePeopleModal && (
        <div className="modal-backdrop">
          <div className="remove-people-modal transfer-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Remove people — {removePeopleModal.suffix}</h3>
              <button
                type="button"
                className="btn ghost"
                onClick={closeRemovePeopleModal}
                disabled={removingPeopleSuffix === removePeopleModal.suffix}
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="modal-body">
              <p>
                Remove all {removePeopleModal.users.length} assigned user(s) from this horizon?
                They will lose access. You can then Delete the horizon on this screen.
              </p>
              <div className="assigned-users-list">
                <ul>
                  {removePeopleModal.users.map((u) => (
                    <li key={u.id}>{u.email} ({u.user_type})</li>
                  ))}
                </ul>
              </div>
              <div className="modal-actions">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={closeRemovePeopleModal}
                  disabled={removingPeopleSuffix === removePeopleModal.suffix}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-warning"
                  disabled={removingPeopleSuffix === removePeopleModal.suffix}
                  onClick={() => handleRemovePeople(removePeopleModal.suffix)}
                >
                  {removingPeopleSuffix === removePeopleModal.suffix ? (
                    <i className="fas fa-spinner fa-spin"></i>
                  ) : (
                    <>
                      <i className="fas fa-user-minus"></i> Remove All People
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SuperAdminSettingsPage;

